import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
// --- Warehouses ---------------------------------------------------------------------
export const warehouseType = z.enum(['operated', 'remote', 'demo']);
export const storageLogic = z.enum(['fixed', 'chaos']);
export const warehouseAddress = z.object({
    name: z.string().max(100).default(''),
    street1: z.string().max(200).default(''),
    street2: z.string().max(200).nullable().default(null),
    zip: z.string().max(20).default(''),
    city: z.string().max(100).default(''),
    countryCode: z.string().length(2).default('DK'),
    phone: z.string().max(40).nullable().default(null),
    email: z.string().max(200).nullable().default(null),
});
export const warehouseDto = z.object({
    id: uuid,
    code: z.string(),
    name: z.string(),
    type: warehouseType,
    storageLogic,
    address: warehouseAddress.nullable(),
    leadTimeDays: z.number().int().nullable(),
    isDefaultReceiving: z.boolean(),
    active: z.boolean(),
    locationCount: z.number().int(),
    createdAt: isoDateTime,
});
export const createWarehouseRequest = z.object({
    code: z.string().trim().min(1).max(10).regex(/^[A-ZÆØÅ0-9_-]+$/i, 'letters, digits, - and _ only'),
    name: z.string().trim().min(1).max(100),
    type: warehouseType.default('operated'),
    storageLogic: storageLogic.default('fixed'),
    address: warehouseAddress.nullable().optional(),
    leadTimeDays: z.number().int().min(0).nullable().optional(),
    isDefaultReceiving: z.boolean().optional(),
});
export const updateWarehouseRequest = createWarehouseRequest.partial().extend({ active: z.boolean().optional() });
// --- Locations ------------------------------------------------------------------------
export const locationType = z.enum(['picking', 'bulk', 'receiving', 'staging', 'pallet', 'quarantine']);
export const locationDto = z.object({
    id: uuid,
    warehouseId: uuid,
    warehouseCode: z.string(),
    code: z.string(),
    row: z.string().nullable(),
    section: z.number().int().nullable(),
    floor: z.number().int().nullable(),
    type: locationType,
    barcode: z.string(),
    capacityHint: z.string().nullable(),
    active: z.boolean(),
    /** Distinct variants with stock on the location. */
    variantCount: z.number().int(),
    onHand: z.number().int(),
});
export const locationQuery = z.object({
    warehouseId: uuid.optional(),
    type: locationType.optional(),
    q: z.string().trim().max(50).optional(),
    includeInactive: z.coerce.boolean().optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(500).default(200),
});
export const createLocationRequest = z.object({
    warehouseId: uuid,
    code: z.string().trim().min(1).max(20).regex(/^[A-Z0-9ÆØÅ_.-]+( [A-Z0-9ÆØÅ_.-]+)*$/i, 'letters, digits, . _ - and single spaces'),
    type: locationType.default('picking'),
    barcode: z.string().trim().max(40).optional(),
    capacityHint: z.string().trim().max(100).nullable().optional(),
});
export const updateLocationRequest = z.object({
    type: locationType.optional(),
    barcode: z.string().trim().min(1).max(40).optional(),
    capacityHint: z.string().trim().max(100).nullable().optional(),
    active: z.boolean().optional(),
});
/**
 * Bulk upload: one location per line, `code[;type[;barcode]]`, separators `;`, `,` or tab.
 * Existing codes are updated (type, barcode) rather than duplicated.
 */
export const uploadLocationsRequest = z.object({
    warehouseId: uuid,
    text: z.string().min(1).max(200_000),
    defaultType: locationType.default('picking'),
});
export const uploadLocationsResult = z.object({
    created: z.number().int(),
    updated: z.number().int(),
    rejected: z.array(z.object({ line: z.number().int(), text: z.string(), reason: z.string() })),
});
export const printLocationLabelsRequest = z.object({
    printerId: uuid,
    locationIds: z.array(uuid).min(1).max(2000),
});
// --- Stock ledger and balances --------------------------------------------------------
export const movementType = z.enum(['receipt', 'pick', 'pack_adjust', 'transfer_out', 'transfer_in', 'count', 'adjust', 'return', 'scrap', 'migration']);
export const stockMovementDto = z.object({
    id: uuid,
    variantId: uuid,
    sku: z.string(),
    variantTitle: z.string(),
    productTitle: z.string(),
    warehouseId: uuid,
    warehouseCode: z.string(),
    locationId: uuid,
    locationCode: z.string(),
    quantity: z.number().int(),
    type: movementType,
    reasonCode: z.string().nullable(),
    note: z.string().nullable(),
    refType: z.string().nullable(),
    refId: z.string().nullable(),
    actorName: z.string(),
    occurredAt: isoDateTime,
});
export const stockMovementQuery = z.object({
    variantId: uuid.optional(),
    warehouseId: uuid.optional(),
    locationId: uuid.optional(),
    type: movementType.optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export const adjustReason = z.enum(['count_correction', 'damaged', 'lost', 'found', 'sample', 'expired', 'other']);
/** A manual adjustment or scrap: a signed quantity on one location, with a reason. */
export const adjustStockRequest = z.object({
    variantId: uuid,
    locationId: uuid,
    quantity: z.number().int().refine((n) => n !== 0, 'quantity must not be zero'),
    type: z.enum(['adjust', 'scrap']).default('adjust'),
    reasonCode: adjustReason,
    note: z.string().trim().max(500).nullable().optional(),
});
/** Per-warehouse figures for one variant; `available` is null for remote warehouses (their stock is callable, not sellable). */
export const warehouseStockDto = z.object({
    warehouseId: uuid,
    warehouseCode: z.string(),
    warehouseType,
    onHand: z.number().int(),
    reserved: z.number().int(),
    available: z.number().int().nullable(),
});
/** Navision's view of a SKU from the nightly bridge sync; the reference for opening counts. */
export const navisionItemDto = z.object({
    sku: z.string(),
    quantity: z.number(),
    sales: z.number(),
    purchases: z.number(),
    productGroup: z.string().nullable(),
    locationCode: z.string().nullable(),
    binCode: z.string().nullable(),
    fetchedAt: isoDateTime,
});
/** Stock per NAV bin from Warehouse Entry, plus the default bin flag from Bin Content. */
export const navisionBinStockDto = z.object({
    locationCode: z.string(),
    binCode: z.string(),
    sku: z.string(),
    quantity: z.number(),
    isDefault: z.boolean(),
});
export const navisionLocationDto = z.object({
    code: z.string(),
    name: z.string(),
    bins: z.number().int(),
    binsWithStock: z.number().int(),
    skus: z.number().int(),
    /** Our warehouse this NAV location maps to, when one exists. */
    warehouseId: uuid.nullable(),
    warehouseCode: z.string().nullable(),
});
export const navisionStatusDto = z.object({
    enabled: z.boolean(),
    mode: z.enum(['sql', 'x3', 'bridge']),
    baseUrl: z.string(),
    keySet: z.boolean(),
    itemCount: z.number().int(),
    binCount: z.number().int(),
    locations: z.array(navisionLocationDto),
    lastFetchedAt: isoDateTime.nullable(),
    lastRun: z.object({ startedAt: isoDateTime, ok: z.boolean().nullable(), fetched: z.number().int(), message: z.string().nullable() }).nullable(),
});
export const variantStockDto = z.object({
    variantId: uuid,
    sku: z.string(),
    variantTitle: z.string(),
    productTitle: z.string(),
    productNumber: z.string().nullable(),
    active: z.boolean(),
    warehouses: z.array(warehouseStockDto),
    onHand: z.number().int(),
    reserved: z.number().int(),
    sellable: z.number().int(),
    callable: z.number().int(),
    navision: navisionItemDto.nullable(),
});
export const stockQuery = z.object({
    q: z.string().trim().max(100).optional(),
    warehouseId: uuid.optional(),
    /** Only variants with stock somewhere (default: true). */
    withStock: z.coerce.boolean().default(true),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export const locationBalanceDto = z.object({
    warehouseId: uuid,
    warehouseCode: z.string(),
    locationId: uuid,
    locationCode: z.string(),
    locationType,
    onHand: z.number().int(),
});
export const reservationDto = z.object({
    id: uuid,
    warehouseId: uuid,
    warehouseCode: z.string(),
    quantity: z.number().int(),
    kind: z.enum(['order', 'shop', 'gu']),
    refType: z.string().nullable(),
    refId: z.string().nullable(),
    expiresAt: isoDateTime.nullable(),
});
export const variantStockDetailDto = variantStockDto.extend({
    locations: z.array(locationBalanceDto),
    reservations: z.array(reservationDto),
});
// --- Counts -----------------------------------------------------------------------------
export const countScope = z.enum(['locations', 'variants']);
export const countStatus = z.enum(['open', 'counted', 'posted', 'cancelled']);
export const countSessionDto = z.object({
    id: uuid,
    warehouseId: uuid,
    warehouseCode: z.string(),
    name: z.string(),
    scope: countScope,
    /** `opening` sessions post `migration` movements (phase 1 opening balances); `cycle` sessions post `count`. */
    kind: z.enum(['cycle', 'opening']),
    status: countStatus,
    lineCount: z.number().int(),
    countedLines: z.number().int(),
    varianceLines: z.number().int(),
    createdBy: z.string(),
    createdAt: isoDateTime,
    postedAt: isoDateTime.nullable(),
});
export const countLineDto = z.object({
    id: uuid,
    variantId: uuid,
    sku: z.string(),
    variantTitle: z.string(),
    productTitle: z.string(),
    locationId: uuid,
    locationCode: z.string(),
    expected: z.number().int(),
    /** Navision's quantity for the SKU on this bin when known, otherwise across all locations: the reference during the opening count. */
    navisionQty: z.number().nullable(),
    counted: z.number().int().nullable(),
    variance: z.number().int().nullable(),
    /** For the handheld: item EANs from Navision and the location's own barcode. */
    barcodes: z.array(z.string()).default([]),
    locationBarcode: z.string().default(''),
    countedBy: z.string().nullable(),
    countedAt: isoDateTime.nullable(),
});
export const countSessionDetailDto = countSessionDto.extend({ lines: z.array(countLineDto) });
/** Locations from Navision's bin list for one NAV location into one of our warehouses. */
export const importNavisionLocationsRequest = z.object({
    warehouseId: uuid,
    navLocationCode: z.string().trim().min(1).max(20),
    /** Bins without stock in Navision are skipped unless this is set. */
    includeEmpty: z.boolean().default(false),
});
/** An opening count whose lines are Navision's stock per bin for one NAV location. */
export const createCountFromNavisionRequest = z.object({
    warehouseId: uuid,
    navLocationCode: z.string().trim().min(1).max(20),
    name: z.string().trim().min(1).max(100),
});
export const createCountFromNavisionResult = z.object({
    sessionId: uuid,
    lines: z.number().int(),
    unknownSkus: z.array(z.string()),
    missingLocations: z.array(z.string()),
});
export const createCountSessionRequest = z.object({
    warehouseId: uuid,
    name: z.string().trim().min(1).max(100),
    scope: countScope,
    kind: z.enum(['cycle', 'opening']).default('cycle'),
    /** Locations to count (scope `locations`); empty means every active location of the warehouse. */
    locationIds: z.array(uuid).max(5000).optional(),
    /** Variants to count (scope `variants`) across all locations of the warehouse. */
    variantIds: z.array(uuid).max(5000).optional(),
});
/** Counted quantities; a line can also be added for a SKU found on a location that had no balance. */
export const enterCountRequest = z.object({
    lines: z.array(z.object({ lineId: uuid, counted: z.number().int().min(0).nullable() })).max(5000).default([]),
    add: z.array(z.object({ sku: z.string().trim().min(1), locationCode: z.string().trim().min(1), counted: z.number().int().min(0) })).max(1000).default([]),
});
//# sourceMappingURL=stock.js.map